/**
 * Rectorium — Architecture Capability-Based
 *
 * SOURCE DE VÉRITÉ : capability-registry.json
 * Ce fichier ne contient AUCUNE donnée métier codée en dur.
 * Il dérive et réexporte les constantes depuis le registre central.
 *
 * Toute modification doit se faire dans capability-registry.json.
 */

import REGISTRY from './capability-registry.json';

// ── Domaines ──────────────────────────────────────────────────────────────────
/*
 * Axe de PORTÉE FONCTIONNELLE, jamais commercial. Il sert à regrouper les 51
 * capacités dans les écrans de gouvernance, rien d'autre.
 *
 * Il s'appelait `tier` et portait les noms des anciens forfaits (foundation,
 * university, enterprise…), ce qui laissait croire qu'une capacité pouvait
 * être vendue à part. La tarification en vigueur est un forfait par tranche
 * de volume d'utilisateurs actifs, sur 4 plans, et CHAQUE TENANT ACCÈDE À
 * 100 % DE L'APPLICATION — aucune capacité ne se refuse pour un motif de
 * facturation. Le registre ne porte donc plus AUCUNE donnée commerciale.
 */
// ── Tableau canonique des capacités ───────────────────────────────────────────
export const CAPABILITIES = REGISTRY.capabilities.map(cap => ({
  id:        cap.id,
  domaine:   cap.domaine,
  status:    cap.status,
  label:     cap.label,
  labelFull: cap.labelFull,
  actions:   Object.keys(cap.permissions),
}));

// ── Accès rapide par id ────────────────────────────────────────────────────────
export const CAPABILITIES_MAP = Object.fromEntries(
  CAPABILITIES.map(c => [c.id, c])
);

// ── Dépendances entre capacités ───────────────────────────────────────────────
export const CAPABILITY_DEPENDENCIES = REGISTRY.capability_dependencies;

// ── Types de portails ─────────────────────────────────────────────────────────
export const PORTAL_TYPES = {
  EXECUTIVE_PORTAL:            'EXECUTIVE_PORTAL',
  ACADEMIC_MANAGEMENT_PORTAL:  'ACADEMIC_MANAGEMENT_PORTAL',
  TEACHING_PORTAL:             'TEACHING_PORTAL',
  LEARNER_PORTAL:              'LEARNER_PORTAL',
  FINANCE_PORTAL:              'FINANCE_PORTAL',
  HR_PORTAL:                   'HR_PORTAL',
  QUALITY_PORTAL:              'QUALITY_PORTAL',
  RESEARCH_PORTAL:             'RESEARCH_PORTAL',
  PARENT_PORTAL:               'PARENT_PORTAL',
  PLATFORM_ADMIN_PORTAL:       'PLATFORM_ADMIN_PORTAL',
  LIMITED_PORTAL:              'LIMITED_PORTAL',
};

// ── Règles de routage de portail ──────────────────────────────────────────────
export const PORTAL_ROUTING_RULES = REGISTRY.portal_routing_rules;

// ── Capacités regroupées par domaine ──────────────────────────────────────────
export const CAPABILITIES_BY_DOMAINE = CAPABILITIES.reduce((acc, cap) => {
  acc[cap.domaine] = acc[cap.domaine] || [];
  acc[cap.domaine].push(cap);
  return acc;
}, {});

const capabilityIdsForAction = (systemRoleId, action) => REGISTRY.capabilities
  .filter(cap => (cap.permissions?.[action] || []).includes(systemRoleId))
  .map(cap => cap.id);

// ── Rôles système (dérivés du registre) ──────────────────────────────────────
export const SYSTEM_ROLES = REGISTRY.system_roles.map(role => {
  if (role.capability_access === 'ALL') {
    const allIds = CAPABILITIES.map(c => c.id);
    return {
      id:    role.id,
      label: role.label,
      color: role.color,
      capabilities: {
        read:    allIds,
        write:   allIds,
        approve: allIds,
        export:  allIds,
        admin:   allIds,
        read_restricted: allIds,
      },
    };
  }
  const allIds = CAPABILITIES.map(c => c.id);
  const toArr = v => (v === 'ALL' ? allIds : v || []);
  return {
    id:    role.id,
    label: role.label,
    color: role.color,
    capabilities: {
      read:    toArr(role.capabilities_read),
      write:   toArr(role.capabilities_write),
      approve: toArr(role.capabilities_approve),
      export:  toArr(role.capabilities_export ?? capabilityIdsForAction(role.id, 'export')),
      admin:   toArr(role.capabilities_admin ?? []),
      read_restricted: toArr(role.capabilities_read_restricted ?? capabilityIdsForAction(role.id, 'read_restricted')),
    },
  };
});

// ── Accès direct au registre complet ─────────────────────────────────────────
export { REGISTRY as CAPABILITY_REGISTRY };
